import type { SavedWorkOrder } from '../types';
import { loadWorkOrders, saveWorkOrders } from './index';

export const WO_RETURN_MAPPING_VERSION = 'service-laama-woreturn-v1' as const;

export interface WoReturnMapping {
  laamaId: string;
  laamaDocumentNumber: string;
  masterWorkOrderNumber: string;
}

export interface WoReturnData {
  mappingVersion: typeof WO_RETURN_MAPPING_VERSION;
  generatedAt: string;
  mappings: WoReturnMapping[];
}

export interface WoReturnApplyResult {
  applied: number;
  alreadySet: number;
  unmatched: string[];
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Validate and normalize a raw parsed return-mapping JSON payload.
 * Throws with a descriptive message on schema violations.
 */
export function parseWoReturnMapping(raw: unknown): WoReturnData {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error('Return mapping must be a JSON object');
  }
  const record = raw as Record<string, unknown>;
  if (record.mappingVersion !== WO_RETURN_MAPPING_VERSION) {
    throw new Error(`mappingVersion must be ${WO_RETURN_MAPPING_VERSION}`);
  }
  if (!Array.isArray(record.mappings)) {
    throw new Error('mappings must be an array');
  }
  const mappings = record.mappings.map((entry, index) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      throw new Error(`mappings[${index}] must be an object`);
    }
    const item = entry as Record<string, unknown>;
    const masterWorkOrderNumber = asText(item.masterWorkOrderNumber);
    if (!/^WO-\d{4}-\d{4}$/.test(masterWorkOrderNumber)) {
      throw new Error(`mappings[${index}].masterWorkOrderNumber must match WO-YYYY-####`);
    }
    const laamaId = asText(item.laamaId);
    const laamaDocumentNumber = asText(item.laamaDocumentNumber);
    if (!laamaId && !laamaDocumentNumber) {
      throw new Error(`mappings[${index}] needs laamaId or laamaDocumentNumber`);
    }
    return { laamaId, laamaDocumentNumber, masterWorkOrderNumber };
  });
  return {
    mappingVersion: WO_RETURN_MAPPING_VERSION,
    generatedAt: asText(record.generatedAt),
    mappings,
  };
}

/**
 * Apply a return mapping to stored work orders.
 * Match by Laama `id` first (D9 primary), then `documentNumber` (audit fallback).
 * A mapping whose number is already set on the matched document counts as
 * alreadySet; a differing existing number is overwritten (master owns the id).
 */
export function applyWoReturnMapping(data: WoReturnData): WoReturnApplyResult {
  const workOrders = loadWorkOrders();
  let applied = 0;
  let alreadySet = 0;
  const unmatched: string[] = [];

  for (const mapping of data.mappings) {
    const match: SavedWorkOrder | undefined =
      (mapping.laamaId && workOrders.find((wo) => wo.id === mapping.laamaId)) ||
      (mapping.laamaDocumentNumber &&
        workOrders.find((wo) => wo.documentNumber === mapping.laamaDocumentNumber)) ||
      undefined;

    if (!match) {
      unmatched.push(mapping.laamaDocumentNumber || mapping.laamaId);
      continue;
    }
    if (match.masterWorkOrderNumber === mapping.masterWorkOrderNumber) {
      alreadySet += 1;
      continue;
    }
    match.masterWorkOrderNumber = mapping.masterWorkOrderNumber;
    match.updatedAt = new Date().toISOString();
    applied += 1;
  }

  if (applied > 0) {
    saveWorkOrders(workOrders);
  }
  return { applied, alreadySet, unmatched };
}

/**
 * Import a return-mapping file produced by the approved master import (F5)
 * and stamp master work-order numbers onto matching Laama documents.
 */
export async function importWoReturnFile(
  file: File
): Promise<{ success: true; result: WoReturnApplyResult } | { success: false; error: string }> {
  try {
    const text = await file.text();
    const data = parseWoReturnMapping(JSON.parse(text));
    return { success: true, result: applyWoReturnMapping(data) };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Invalid return mapping file',
    };
  }
}
